export interface ManagerUpdate {
	employeeExternalId: string
	employeeName?: string
	primaryManagerExternalId?: string
	primaryManagerName?: string
	highLevelManagerExternalId?: string
	secondaryManagerName?: string
	coManagerExternalIds?: string[]
	coManagerNames?: string[]
	hasCompletedManagerEvaluation?: boolean
}

export interface ColumnMapping {
	employeeId: string
	primaryManagerId: string
	secondaryManagerId: string
	coManagerId: string
}

/**
 * Detect CSV delimiter and quote character from the header line.
 * Tries common delimiters (comma, semicolon, tab) and quote chars (double, single).
 */
export const detectCSVFormat = (headerLine: string): { delimiter: string; quoteChar: string } => {
	const delimiters = [',', ';', '\t']
	const quoteChars = ['"', "'"]

	let detectedDelimiter = ','
	let maxCount = 0
	for (const d of delimiters) {
		const count = headerLine.split(d).length - 1
		if (count > maxCount) {
			maxCount = count
			detectedDelimiter = d
		}
	}

	let detectedQuote = '"'
	for (const q of quoteChars) {
		if (
			headerLine.startsWith(q) ||
			headerLine.includes(`${detectedDelimiter}${q}`) ||
			headerLine.includes(`${q}${detectedDelimiter}`)
		) {
			detectedQuote = q
			break
		}
	}

	return { delimiter: detectedDelimiter, quoteChar: detectedQuote }
}

const sanitizeFileValue = (value?: unknown): string | undefined => {
	return value
		? String(value)
				.replace(/[\u2060\u200B\u200C\u200D]/g, '')
				.trim()
		: undefined
}

/**
 * Parse a CSV line handling quoted values with embedded delimiters.
 * e.g., 'a,"b,c",d' -> ['a', 'b,c', 'd']
 */
export const parseCSVLine = (line: string, delimiter = ',', quoteChar = '"'): string[] => {
	const result: string[] = []
	let current = ''
	let inQuotes = false

	for (let i = 0; i < line.length; i++) {
		const char = line[i]

		if (char === quoteChar) {
			if (inQuotes && line[i + 1] === quoteChar) {
				current += quoteChar
				i++
			} else {
				inQuotes = !inQuotes
			}
		} else if (char === delimiter && !inQuotes) {
			result.push(sanitizeFileValue(current) || '')
			current = ''
		} else {
			current += char
		}
	}

	result.push(sanitizeFileValue(current) || '')
	return result
}

export const extractCSVHeaders = (text: string): { headers: string[]; delimiter: string; quoteChar: string } => {
	const lines = text.split('\n').filter((line) => line.trim())
	if (lines.length === 0) return { headers: [], delimiter: ',', quoteChar: '"' }

	const { delimiter, quoteChar } = detectCSVFormat(lines[0])
	const headers = parseCSVLine(lines[0], delimiter, quoteChar)

	return { headers, delimiter, quoteChar }
}

export const parseCSVWithMapping = (
	text: string,
	mapping: ColumnMapping,
	delimiter = ',',
	quoteChar = '"',
): ManagerUpdate[] => {
	const lines = text.split('\n').filter((line) => line.trim())
	if (lines.length < 2) return []

	const headers = parseCSVLine(lines[0], delimiter, quoteChar)
	const updates: ManagerUpdate[] = []

	const employeeIdIndex = headers.indexOf(mapping.employeeId)
	const primaryManagerIndex = mapping.primaryManagerId ? headers.indexOf(mapping.primaryManagerId) : -1
	const secondaryManagerIndex = mapping.secondaryManagerId ? headers.indexOf(mapping.secondaryManagerId) : -1
	const coManagerIndex = mapping.coManagerId ? headers.indexOf(mapping.coManagerId) : -1

	if (employeeIdIndex === -1) {
		throw new Error('Employee ID column not found in CSV')
	}

	for (let i = 1; i < lines.length; i++) {
		const values = parseCSVLine(lines[i], delimiter, quoteChar)
		const employeeId = values[employeeIdIndex]

		if (employeeId) {
			const coManagerRaw = coManagerIndex !== -1 ? values[coManagerIndex] || '' : ''
			// Support comma, semicolon, or whitespace as separators
			const coManagerExternalIds = coManagerRaw
				? coManagerRaw
						.split(/[,;\s]+/)
						.map((id) => id.trim())
						.filter((id) => id.length > 0)
				: []

			if (coManagerExternalIds.length > 10) {
				throw new Error(
					`Row ${i + 1}: Employee ${employeeId} has ${coManagerExternalIds.length} co-managers. Maximum allowed is 10.`,
				)
			}

			updates.push({
				employeeExternalId: employeeId,
				primaryManagerExternalId: primaryManagerIndex !== -1 ? values[primaryManagerIndex] || undefined : undefined,
				highLevelManagerExternalId:
					secondaryManagerIndex !== -1 ? values[secondaryManagerIndex] || undefined : undefined,
				coManagerExternalIds: coManagerExternalIds.length > 0 ? coManagerExternalIds : undefined,
			})
		}
	}

	return updates
}
